import { zodResolver } from "@hookform/resolvers/zod";
import { Wallet, Loader2 } from "lucide-react";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

import { InfoBlock } from "@/shared/ui/info-block";
import { InfoTooltip } from "@/shared/ui/info-tooltip";
import { Button } from "@/shared/ui/kit/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/ui/kit/form";
import { Input } from "@/shared/ui/kit/input";
import { Price } from "@/shared/ui/price";
import { ResponsiveModal } from "@/shared/ui/responsive-modal";

import { useCreateWithdrawal } from "../model/use-create-withdrawal";

const TRON_ADDRESS_REGEX = /^[T][a-km-zA-HJ-NP-Z1-9]{33}$/;

type WithdrawDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableBalance: number;
  modal?: boolean;
};

export function WithdrawDialog({
  open,
  onOpenChange,
  availableBalance,
  modal = true,
}: WithdrawDialogProps) {
  const { t } = useTranslation();

  const createWithdrawal = useCreateWithdrawal();

  const formSchema = useMemo(
    () =>
      z.object({
        amount: z
          .string({ message: t("withdrawal.errors.invalidAmount") })
          .refine((val) => {
            const num = parseFloat(val);
            return !isNaN(num) && num > 0;
          }, t("withdrawal.errors.invalidAmount"))
          .refine((val) => {
            const num = parseFloat(val);
            const cents = Math.round(num * 100);
            return cents <= availableBalance;
          }, t("withdrawal.errors.insufficientBalance")),
        walletAddress: z
          .string({ message: t("withdrawal.errors.invalidWallet") })
          .regex(TRON_ADDRESS_REGEX, t("withdrawal.errors.invalidWallet")),
      }),
    [t, availableBalance],
  );

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      amount: "",
      walletAddress: "",
    },
  });

  const handleSubmit = form.handleSubmit(async (data) => {
    const amountCents = Math.round(parseFloat(data.amount) * 100);

    try {
      await createWithdrawal.mutateAsync({
        body: {
          withdrawal: {
            idempotency_key: crypto.randomUUID(),
            amount_cents: amountCents,
            wallet_address: data.walletAddress,
          },
        },
      });

      toast.success(t("withdrawal.success"));
      form.reset();
      onOpenChange(false);
    } catch (err: unknown) {
      const apiError = err as { error?: { code?: string; message?: string } };

      if (apiError.error?.code === "insufficient_balance") {
        toast.error(t("withdrawal.errors.insufficientBalance"));
      } else if (apiError.error?.message?.includes("blacklisted")) {
        toast.error(t("withdrawal.errors.walletBlacklisted"));
      } else if (apiError.error?.message?.includes("wallet_address")) {
        toast.error(t("withdrawal.errors.invalidWallet"));
      } else {
        toast.error(
          apiError.error?.message || t("withdrawal.errors.createFailed"),
        );
      }
    }
  });

  const handleClose = () => {
    form.reset();
    onOpenChange(false);
  };

  return (
    <ResponsiveModal open={open} onOpenChange={handleClose} modal={modal}>
      <ResponsiveModal.Content className="max-w-md">
        <ResponsiveModal.Header>
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10">
              <Wallet className="h-4 w-4 text-primary" />
            </div>
            <div className="flex flex-col gap-1">
              <ResponsiveModal.Title>
                {t("withdrawal.title")}
              </ResponsiveModal.Title>
              <ResponsiveModal.Description>
                {t("withdrawal.description")}
              </ResponsiveModal.Description>
            </div>
          </div>
        </ResponsiveModal.Header>

        <ResponsiveModal.Body>
          <Form {...form}>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="rounded-lg bg-muted/50 p-3">
                <div className="text-xs text-muted-foreground mb-1">
                  {t("withdrawal.availableBalance")}
                </div>
                <div className="text-xl font-bold text-primary">
                  <Price cents={availableBalance} />
                </div>
              </div>

              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("withdrawal.amount")}</FormLabel>
                    <FormControl>
                      <div className="relative flex gap-2">
                        <div className="relative flex-1">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            {...field}
                            className="pr-12"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                            USD
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            const dollars = availableBalance / 100;
                            field.onChange(dollars.toFixed(2));
                          }}
                          className="shrink-0 h-full"
                        >
                          {t("withdrawal.all")}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InfoBlock
                variant="warning"
                titleKey="withdrawal.info.tronWarningTitle"
                bodyKey="withdrawal.info.tronWarningBody"
              />

              <FormField
                control={form.control}
                name="walletAddress"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="inline-flex items-center gap-1.5">
                      {t("withdrawal.walletAddress")}
                      <InfoTooltip tKey="withdrawal.info.tronOnly" />
                    </FormLabel>
                    <FormControl>
                      <Input type="text" placeholder="T..." {...field} />
                    </FormControl>
                    <FormMessage />
                    <p className="text-xs text-muted-foreground">
                      {t("withdrawal.walletHint")}
                    </p>
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </ResponsiveModal.Body>

        <ResponsiveModal.Footer>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={createWithdrawal.isPending}
              className="flex-1 h-10"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={() => handleSubmit()}
              disabled={!form.formState.isValid || createWithdrawal.isPending}
              className="flex-1 h-10"
            >
              {createWithdrawal.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {t("withdrawal.submit")}
            </Button>
          </div>
        </ResponsiveModal.Footer>
      </ResponsiveModal.Content>
    </ResponsiveModal>
  );
}
